import React, { useState } from 'react';

import { Globe, MapPin, Phone, Users } from 'react-feather';
import { useDispatch } from 'react-redux';

import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';

import styles from './BusinessInfoTopSection.module.scss';

/**
 * Business identity header shown at the top of BusinessesDetails /
 * AffiliateBusinessDetails — logo, name, categories, follower count, a
 * follow/unfollow button, and contact info (address, phone, website).
 *
 * This component didn't exist anywhere (assumed to already be part of the
 * shared `components` library, like CustomerCouponCard) — built here based
 * on the business data shape used consistently across every other
 * converted screen: { name, logo, categories, followers_count, street,
 * city: { name }, postal_code, phone, business_web_url }.
 *
 * Props:
 *   data        — business object
 *   isFollowing — boolean, current follow state for this user
 */
const getInitials = name => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const BusinessInfoTopSection = ({ data, isFollowing }) => {
  const dispatch = useDispatch();
  const [logoFailed, setLogoFailed] = useState(false);
  const [followPending, setFollowPending] = useState(false);

  const {
    id,
    name,
    logo,
    categories,
    followers_count: followersCount,
    street,
    city,
    postal_code: postalCode,
    phone,
    business_web_url: businessWebUrl,
  } = data || {};

  const addressParts = [street, city?.name, postalCode].filter(Boolean);
  const address = addressParts.join(', ');

  const handleFollowToggle = () => {
    if (!id) return;
    setFollowPending(true);
    if (isFollowing) {
      dispatch(followActions.unfollowBusinessRequest({ business_id: id }));
    } else {
      dispatch(followActions.followBusinessRequest({ business_id: id }));
    }
    dispatch(followActions.getFollowedBusinessesRequest());
    // No resolve/callback exists on these actions (fire-and-forget pattern
    // used everywhere else in the app), so just clear the pending state
    // optimistically after dispatch.
    setFollowPending(false);
  };

  const openPhoneNumber = () => {
    if (phone) window.location.href = `tel:${phone}`;
  };

  const openWebsite = () => {
    if (!businessWebUrl) return;
    const withScheme = /^https?:\/\//i.test(businessWebUrl) ? businessWebUrl : `https://${businessWebUrl}`;
    window.open(withScheme, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.headerRow}>
        {logo && !logoFailed ? (
          <img src={logo} alt={name || ''} className={styles.logo} onError={() => setLogoFailed(true)} />
        ) : (
          <div className={styles.logoFallback}>{getInitials(name)}</div>
        )}

        <div className={styles.identity}>
          <h1 className={styles.name}>{name}</h1>

          {categories?.length > 0 && (
            <div className={styles.categories}>
              {categories.map(cat => (
                <span key={cat.id ?? cat.name} className={styles.categoryChip}>
                  {cat.name}
                </span>
              ))}
            </div>
          )}

          {typeof followersCount === 'number' && (
            <div className={styles.followersRow}>
              <Users size={14} />
              <span>
                {followersCount} {followersCount === 1 ? 'follower' : 'followers'}
              </span>
            </div>
          )}
        </div>

        <button
          type="button"
          className={isFollowing ? `${styles.followBtn} ${styles.following}` : styles.followBtn}
          onClick={handleFollowToggle}
          disabled={followPending}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </button>
      </div>

      {(address || phone || businessWebUrl) && (
        <div className={styles.contactRow}>
          {address && (
            <span className={styles.contactItem}>
              <MapPin size={15} className={styles.contactIcon} />
              {address}
            </span>
          )}
          {phone && (
            <button type="button" className={styles.contactItem} onClick={openPhoneNumber}>
              <Phone size={15} className={styles.contactIcon} />
              {phone}
            </button>
          )}
          {businessWebUrl && (
            <button type="button" className={styles.contactItem} onClick={openWebsite}>
              <Globe size={15} className={styles.contactIcon} />
              Visit website
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessInfoTopSection;